export interface LoginRequest {
  email: string;
  password: string;
  tenantId?: string;
}

export interface RegisterRequest {
  tenantSlug: string;
  tenantName: string;
  email: string;
  fullName: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  refreshToken?: string;
}

export interface RegisterResponse {
  token: string;
}

export interface AuthUser {
  userId: string;
  tenantId: string;
  email: string;
  role: string;
  isAuthenticated: boolean;
  permissions?: string[];
}

export interface SystemSettings {
  [key: string]: unknown;
}

export interface PlatformTeamsAppSettingsDto {
  exists: boolean;
  isReady: boolean;
  isBotConfigured: boolean;
  missingFields: string[];
  clientId: string;
  operatorTenantId?: string | null;
  microsoftAppId: string;
  botEndpoint: string;
  redirectUri: string;
  isEnabled: boolean;
  clientSecretConfigured: boolean;
  clientSecretRotatedAt?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface UpsertPlatformTeamsAppRequest {
  clientId: string;
  operatorTenantId?: string | null;
  microsoftAppId?: string | null;
  botEndpoint?: string | null;
  redirectUri: string;
  isEnabled: boolean;
}

export interface SetPlatformTeamsAppSecretRequest {
  clientSecret: string;
}

export interface TestPlatformTeamsAppCredentialsResult {
  success: boolean;
  message?: string | null;
  httpStatusCode?: number | null;
  errorCode?: string | null;
  errorDescription?: string | null;
  tokenType?: string | null;
  expiresIn?: number | null;
}

/**
 * Setup metadata for the operator-facing Teams setup flow. Returned by
 * GET /api/system/integrations/teams/setup-info — used by the Admin Portal to
 * pre-populate the redirect URI / bot endpoint fields and to show the operator
 * exactly what the setup script will register.
 */
export interface TeamsSetupInfoDto {
  apiBaseUrl: string;
  redirectUri: string;
  botEndpoint: string;
  environment: string;
  scriptDownloadUrl: string;
}

export interface TenantSummary {
  id: string;
  name: string;
  isActive: boolean;
  slug?: string;
  tier?: string;
  userCount?: number;
  createdAt?: string;
}

export interface BuiltInUser {
  id: string;
  fullName: string;
  email: string;
  userType?: string;
  roles?: string[];
  lastActiveAt?: string;
  createdAt?: string;
  isSystemUser?: boolean;
}

export interface SystemInfoResponse {
  applicationName?: string;
  version?: string;
  buildNumber?: string;
  environment?: string;
  deployedAt?: string;
  uptime?: string;
  serverTimestamp?: string;
  status?: string;
  runtime?: string;
  components?: ComponentInfo[];
  serviceEndpoints?: {
    name: string;
    url: string;
    status?: string;
  }[];
}

export interface VersionChange {
  category: 'feature' | 'fix' | 'improvement' | 'breaking' | 'security' | 'documentation' | 'test' | 'removed' | 'note';
  description: string;
}

export interface VersionEntry {
  version: string;
  releasedAt: string;
  summary: string;
  changes: VersionChange[];
}

export interface VersionHistoryResponse {
  currentVersion: string;
  versions: VersionEntry[];
}

// Log levels
export type LogLevel = 'Trace' | 'Debug' | 'Information' | 'Warning' | 'Error' | 'Critical';

export interface SystemLog {
  id: string;
  timestamp: string;
  level: LogLevel;
  logger: string;
  message: string;
  exception?: string;
  correlationId?: string;
  userId?: string;
  tenantId?: string;
  requestPath?: string;
  properties?: Record<string, unknown>;
}

export interface SystemLogsResponse {
  items: SystemLog[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// Scheduled Jobs
export interface ScheduledJobDto {
  name: string;
  category?: 'Timer' | 'Queue' | 'HTTP';
  functionApp: string;
  triggerType: 'Timer' | 'ServiceBus' | 'HTTP' | 'Http' | 'Queue';
  schedule?: string;
  queueName?: string;
  frequency?: string;
  frequencyDescription?: string;
  lastRun?: string;
  nextRun?: string;
  status: 'Active' | 'Disabled' | 'Failed' | 'Running';
  description?: string;
}

// Assistant Usage
export interface AssistantUsageRow {
  tenantId: string;
  tenantName: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  interactions: number;
  estimatedCost: number;
}

export interface AssistantUsageSummary {
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  totalInteractions: number;
  totalEstimatedCost: number;
  rows: AssistantUsageRow[];
}

// System Info
export interface ComponentInfo {
  name: string;
  status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  statusMessage?: string;
  version?: string;
  commitSha?: string;
  buildTimestamp?: string;
  deployedAt?: string;
  details?: string;
}

// ITIL Configuration
export interface PriorityLevelDto {
  id?: string;
  name: string;
  value: number;
  color: string;
  description?: string;
  isDefault?: boolean;
}

export interface PriorityMatrixEntryDto {
  impact?: number;
  urgency?: number;
  priorityId?: string;
  impactLevel?: number;
  urgencyLevel?: number;
  priorityLevel?: number;
}

export interface SystemPriorityTemplateDto {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  isActive?: boolean;
  levels: PriorityLevelDto[];
  matrix: PriorityMatrixEntryDto[];
  priorityLevels?: PriorityLevelDto[];
  impactLevels?: PriorityLevelDto[];
  urgencyLevels?: PriorityLevelDto[];
  priorityMatrix?: PriorityMatrixEntryDto[];
  createdAt: string;
  updatedAt?: string;
}

export interface TenantPriorityConfigDto {
  id: string;
  tenantId: string;
  customerId?: string;
  priorityLevels: PriorityLevelDto[];
  impactLevels: PriorityLevelDto[];
  urgencyLevels: PriorityLevelDto[];
  priorityMatrix: PriorityMatrixEntryDto[];
  templateName?: string;
  isUsingSystemDefault?: boolean;
  isActive: boolean;
}

export interface UpsertSystemPriorityTemplateRequest {
  name: string;
  description?: string;
  isDefault?: boolean;
  levels: Omit<PriorityLevelDto, 'id'>[];
  matrix: PriorityMatrixEntryDto[];
  priorityLevels?: Omit<PriorityLevelDto, 'id'>[];
  impactLevels?: Omit<PriorityLevelDto, 'id'>[];
  urgencyLevels?: Omit<PriorityLevelDto, 'id'>[];
  priorityMatrix?: PriorityMatrixEntryDto[];
}

export interface ReasonDefinitionDto {
  id: string;
  label: string;
  value: string;
  description?: string;
  isActive: boolean;
  sortOrder: number;
}

export interface OnHoldReasonDefinitionDto extends ReasonDefinitionDto {
  requiresFollowUp: boolean;
}

export interface SystemReasonTemplateDto {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  resolveReasons: ReasonDefinitionDto[];
  onHoldReasons: OnHoldReasonDefinitionDto[];
  cancelReasons: ReasonDefinitionDto[];
  createdAt: string;
  updatedAt?: string;
}

export interface UpsertSystemReasonTemplateRequest {
  name: string;
  description?: string;
  isDefault?: boolean;
  resolveReasons: Omit<ReasonDefinitionDto, 'id'>[];
  onHoldReasons: Omit<OnHoldReasonDefinitionDto, 'id'>[];
  cancelReasons: Omit<ReasonDefinitionDto, 'id'>[];
}

export interface TenantReasonConfigDto {
  id: string;
  tenantId: string;
  customerId?: string;
  resolveReasons: ReasonDefinitionDto[];
  onHoldReasons: OnHoldReasonDefinitionDto[];
  cancelReasons: ReasonDefinitionDto[];
}

export type ScriptType = 'Bash' | 'PowerShell';
export type ScriptScope = 'System' | 'Tenant' | 'Customer';
export type OsTarget = 'Linux' | 'Windows' | 'Any';

export interface ScriptParameterDefinition {
  name: string;
  description: string;
  required: boolean;
  defaultValue?: string;
}

export interface ScriptDto {
  id: string;
  tenantId: string | null;
  customerId: string | null;
  name: string;
  description: string | null;
  scriptContent: string;
  scriptType: ScriptType;
  osTarget: OsTarget;
  scope: ScriptScope;
  isBuiltIn: boolean;
  isActive: boolean;
  expectedOutputFormat: string | null;
  parameters: ScriptParameterDefinition[] | null;
  systemKey: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface ScriptSummaryDto {
  id: string;
  name: string;
  description: string | null;
  scriptContent: string;
  scriptType: ScriptType;
  osTarget: OsTarget;
  scope: ScriptScope;
  isBuiltIn: boolean;
  isActive: boolean;
  systemKey: string | null;
}

export type ServiceConnectionType = 'ApiKey' | 'OAuth' | 'Basic' | 'Certificate';

export interface ServiceConnectionDto {
  id: string;
  tenantId: string;
  customerId: string;
  name: string;
  type: ServiceConnectionType;
  configuration: string;
  hasSecret: boolean;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export interface ServiceConnectionListDto {
  items: ServiceConnectionDto[];
  totalCount: number;
}

export interface CiClassDto {
  id: string;
  tenantId: string;
  parentClassId?: string;
  name: string;
  code: string;
  icon?: string;
  description?: string;
  level: number;
  inheritsFields: boolean;
  isSystem: boolean;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export const TeamScope = {
  TenantWide: 'TenantWide',
  Customer: 'Customer',
} as const;
export type TeamScope = typeof TeamScope[keyof typeof TeamScope];

export interface Team {
  id: string;
  tenantId: string;
  name: string;
  description: string;
  isActive: boolean;
  isDefault: boolean;
  scope: TeamScope;
  scopeCustomerId?: string;
  scopeCustomerName?: string;
  memberCount: number;
  deletedAt?: string;
  deletedBy?: string;
  deletedByName?: string;
  deletedByUserName?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CorrelationRuleDto {
  id: string;
  tenantId: string;
  name: string;
  description: string;
  isEnabled: boolean;
  priority: number;
  correlationType: string;
  timeWindowMinutes: number;
  maxAlertsPerEpisode: number;
  filterExpression?: string | null;
  correlationKeyFields: string;
  autoEscalate: boolean;
  escalationThreshold: number;
  version: number;
  createdAt: string;
  updatedAt?: string;
}

export interface SlaPriorityTarget {
  priorityId: string;
  priorityName: string;
  responseTimeMinutes: number | null;
  resolutionTimeMinutes: number | null;
}

export interface SystemSlaPolicyTemplateDto {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  businessHoursOnly: boolean;
  targets: SlaPriorityTarget[];
  createdAt: string;
  updatedAt?: string;
}

export interface CreateSystemSlaPolicyTemplateDto {
  name: string;
  description?: string;
  isDefault?: boolean;
  businessHoursOnly?: boolean;
  targets: Array<{
    priorityId: string;
    priorityName?: string;
    responseTimeMinutes: number | null;
    resolutionTimeMinutes: number | null;
  }>;
}

export interface UpdateSystemSlaPolicyTemplateDto extends CreateSystemSlaPolicyTemplateDto {
  id: string;
}

// Settings
export interface GeneralSettings {
  supportEmail: string;
  defaultTimezone: string;
  invitationExpirationDays?: number;
  ciRetirementGracePeriodDays?: number;
  autoCloseEnabled?: boolean;
  autoCloseDays?: number;
  autoCloseFrequency?: number;
  autoCloseFrequencyMinutes?: number;
}

export interface SendGridSettings {
  apiKey: string;
  fromEmail: string;
  fromName: string;
  enabled?: boolean;
  hasApiKey?: boolean;
}

export interface AssistantSettings {
  basicTierModel: string;
  premiumTierModel: string;
  tenantModelOverrides: Record<string, string>;
}

export interface AiCreditsSettings {
  globalMonthlyProviderCostCapUsd: number | null;
  globalDailyProviderCostCapUsd: number | null;
  emergencyStop: boolean;
  defaultPricePerCreditUsd: number;
  tierDefaults: Record<string, number>;
  tierMaximums: Record<string, number>;
  tierProviderCostCapsUsd: Record<string, number>;
}

export interface IncidentChatTierCeilingsDto {
  trial: number;
  starter: number;
  professional: number;
  enterprise: number;
}

// Incident AI chat default policy (System Admin)
export interface IncidentAiChatPlatformTenantPolicy {
  tenantId: string;
  tenantName: string;
  tier: string;
  isActive: boolean;
  tierCeiling: number;
  /** Per-tenant override; null means the tenant inherits the platform default. */
  overrideLimit: number | null;
  inheritsPlatformDefault: boolean;
  /** Tenant default before the tier ceiling is applied. */
  defaultLimit: number;
  /** Tenant default after the tier ceiling is applied. */
  effectiveDefaultLimit: number;
}

export interface IncidentAiChatPlatformPolicy {
  platformDefaultLimit: number;
  tenants: IncidentAiChatPlatformTenantPolicy[];
}

export interface SystemSettingsTyped {
  general: GeneralSettings;
  sendGrid: SendGridSettings;
  assistant: AssistantSettings;
  aiCredits: AiCreditsSettings;
  incidentChatTierCeilings?: IncidentChatTierCeilingsDto;
  eventRetentionDays?: number;
  deadLetterCount?: number;
  sendGridEnabled?: boolean;
  hasApiKey?: boolean;
  invitationExpirationDays?: number;
  ciRetirementGracePeriodDays?: number;
  autoCloseEnabled?: boolean;
  autoCloseDays?: number;
  autoCloseFrequency?: number;
}

// Sign-in Logs
export interface SignInLog {
  id: string;
  userId: string;
  email: string;
  tenantId: string;
  tenantName?: string;
  timestamp: string;
  ipAddress: string;
  userAgent?: string;
  success: boolean;
  failureReason?: string;
}

export interface SignInLogsResponse {
  items: SignInLog[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// Pipeline Defaults
export interface PipelineDefaultsDto {
  id?: string;
  endpointType: string;
  defaultSteps?: string[];
  configuration?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  validation?: unknown;
  normalization?: unknown;
  deduplication?: unknown;
  suppression?: unknown;
  enrichment?: unknown;
  correlation?: unknown;
  incidentGate?: unknown;
  incidentCreation?: unknown;
  notification?: unknown;
}

export interface PipelineDefaultsListDto {
  endpointTypes: PipelineDefaultsSummaryDto[];
}

export interface PipelineDefaultsSummaryDto {
  endpointType: string;
  hasCustomDefaults: boolean;
  customStepCount: number;
  lastModified?: string;
}

export const EndpointType = {
  AzureMonitor: 'AzureMonitor',
  Datadog: 'Datadog',
  Prometheus: 'Prometheus',
  Generic: 'Generic',
  AwsCloudWatch: 'AwsCloudWatch',
  Grafana: 'Grafana',
  NewRelic: 'NewRelic',
} as const;
export type EndpointType = (typeof EndpointType)[keyof typeof EndpointType];

export const ProcessingStepType = {
  Validation: 'Validation',
  Normalization: 'Normalization',
  Deduplication: 'Deduplication',
  Suppression: 'Suppression',
  Enrichment: 'Enrichment',
  Correlation: 'Correlation',
  IncidentGate: 'IncidentGate',
  IncidentCreation: 'IncidentCreation',
  Notification: 'Notification',
  Initialization: 'Initialization',
  Custom: 'Custom',
} as const;
export type ProcessingStepType =
  (typeof ProcessingStepType)[keyof typeof ProcessingStepType];

export type PipelineNodeType = 'Step' | 'Decision' | 'Merge';

export interface PipelineNodeDto {
  id: string;
  nodeType: PipelineNodeType;
  stepType?: ProcessingStepType;
  name: string;
  positionX: number;
  positionY: number;
  configuration?: string;
  isEnabled: boolean;
  conditionField?: string;
  conditionOperator?: string;
}

export interface PipelineEdgeDto {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  label?: string;
  conditionValue?: string;
  sortOrder: number;
  isDefault: boolean;
}

export type ConditionLogic = 'AND' | 'OR';

export type DecisionOperator =
  | 'equals' | 'notEquals'
  | 'contains' | 'notContains'
  | 'startsWith' | 'endsWith'
  | 'gt' | 'gte' | 'lt' | 'lte' | 'between'
  | 'exists' | 'notExists'
  | 'matches' | 'in' | 'notIn';

export interface ConditionRule {
  field: string;
  operator: DecisionOperator;
  value?: string;
  value2?: string;
}

export interface ConditionGroup {
  logic: ConditionLogic;
  conditions: ConditionRule[];
  groups: ConditionGroup[];
}

export interface PipelineGraphDto {
  pipelineConfigurationId: string;
  nodes: PipelineNodeDto[];
  edges: PipelineEdgeDto[];
}

// Email Templates
export interface EmailTemplateDto {
  id: string;
  name: string;
  subject: string;
  htmlBody: string;
  textBody?: string;
  variables: string[];
  isActive: boolean;
  updatedAt?: string;
}

// Performance
export interface PerformanceSummaryDto {
  averageResponseTimeMs: number;
  p95ResponseTimeMs: number;
  p99ResponseTimeMs: number;
  totalRequests: number;
  errorRate: number;
  slowestEndpoints: {
    path: string;
    averageMs: number;
    count: number;
  }[];
}

export interface EmailLogDto {
  id: string;
  recipientEmail: string;
  recipientName?: string;
  subject?: string;
  status: string;
  triggerType?: string;
  notificationType?: string;
  sentAt: string;
  failedAt?: string;
  errorMessage?: string;
  providerStatusCode?: string;
  fromEmail?: string;
  fromName?: string;
  htmlBody?: string;
}

export interface SignInLogEntry {
  id: string;
  timestamp: string;
  email: string;
  success: boolean;
  ipAddress?: string;
  userAgent?: string;
  authMethod?: string;
  identityProvider?: string;
  errorMessage?: string;
}

export interface SignInLogsDetailedResponse {
  logs: SignInLogEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface BugReportDto {
  id: string;
  title: string;
  description?: string;
  fingerprint: string;
  severity: string;
  occurrenceCount: number;
  lastSeenAt: string;
  status: string;
}

export interface FeedbackDto {
  id: string;
  message: string;
  pageUrl: string;
  feedbackType: string;
  createdAt: string;
  status: string;
  rating?: number;
}

export type SavedNavLinkScope = 'User' | 'Tenant' | 'System';

export interface SavedNavLinkDto {
  id: string;
  scope: SavedNavLinkScope;
  name: string;
  description?: string;
  targetPath: string;
  sectionName?: string;
}

export interface CreateSavedNavLinkRequest {
  scope: SavedNavLinkScope;
  name: string;
  description?: string;
  targetPath: string;
  sectionName?: string;
}

export interface CreateFeedbackRequest {
  pageUrl: string;
  feedbackType: string;
  message: string;
  rating?: number;
}

// AI Credits (System Admin)
export interface SystemAiCreditsOverview {
  totalCreditsUsedAllTenants: number;
  totalTokensAllTenants: number;
  totalEstimatedCost: number;
  totalActualCost: number;
  platformPolicy?: {
    globalMonthlyCostCapUsd: number;
    globalDailyCostCapUsd: number;
    emergencyStop: boolean;
  };
  modelRates?: AiProviderModelRateSummary[];
  tenantBreakdown: TenantAiCreditSummary[];
}

export interface AiProviderModelRateSummary {
  modelName: string;
  displayName?: string;
  baseTokensPerCredit: number;
  multiplier: number;
}

export interface AiBudgetPeriodStatusDto {
  startUtc: string;
  isDaily: boolean;
  usedUsd: number;
  reservedUsd: number;
  accountingBlocked: boolean;
}

export interface AiBudgetStatusDto {
  periods: AiBudgetPeriodStatusDto[];
  unsettledAttempts: number;
  expiredUnsettledAttempts: number;
  internalProviderCostUsd: number;
}

export interface TenantAiCreditSummary {
  tenantId: string;
  tenantName: string;
  tier?: string;
  isActive?: boolean;
  creditsUsed: number;
  creditsRemaining: number;
  entitlementMonthlyCredits?: number;
  effectiveMonthlyCredits?: number;
  entitlementSource?: string;
  monthlyCreditsEntitlementOverride?: number | null;
  monthlyCreditsOperatingLimit?: number | null;
  monthlyProviderCostCapUsdOverride?: number | null;
  monthlyProviderCostCapOperatingLimitUsd?: number | null;
  quotaUpdatedAtUtc?: string | null;
  monthlyCostCapUsd: number | null;
  monthlyProviderCostCapUsd?: number | null;
  totalCustomerCost: number;
  totalActualCost: number;
  remainingCostBudgetUsd: number | null;
  remainingProviderCostBudgetUsd?: number | null;
  pricePerCredit?: number;
  totalTokens: number;
  estimatedCost: number;
}

export interface UpdateTenantAiCreditSettingsRequest {
  isAiEnabled?: boolean;
  monthlyCreditsLimit?: number | null;
  monthlyCostCapUsd?: number | null;
  monthlyCreditsEntitlementOverride?: number | null;
  monthlyCreditsOperatingLimit?: number | null;
  monthlyProviderCostCapUsdOverride?: number | null;
  monthlyProviderCostCapOperatingLimitUsd?: number | null;
  overrideReason?: string;
  expectedQuotaUpdatedAtUtc?: string;
  enableAiResolutionSuggestions?: boolean;
  enableAiBeacon?: boolean;
  enableAiKnowledgeSources?: boolean;
}

export interface AiModelCreditRateDto {
  id: string;
  modelName: string;
  multiplier: number;
  isActive: boolean;
}
